import type { NextPage } from 'next';
import React from 'react';
import { useIntl } from 'react-intl';

import HeroImageStaff from '@/assets/group-rv-bookings/airstream-tropical-patio.jpg';
import StaffCameraImage from '@/assets/group-rv-bookings/cameraman-city-street.jpg';
import FilmProductionFull from '@/assets/group-rv-bookings/film-crew-outdoor-market.jpg';
import HeroImageEvents from '@/assets/group-rv-bookings/music-festival-aerial.jpg';
import StaffRoomImage from '@/assets/group-rv-bookings/rv-interior-living-area.jpg';
import FilmProductionSmall from '@/assets/group-rv-bookings/rvs-parked-campground.jpg';
import HeroImageWeddings from '@/assets/group-rv-bookings/wedding-ceremony-lakeside.jpg';
import { GroupRvEnquiryForm } from '@/components/route/group-rv-bookings/EnquiryForm';
import { GroupRvFaqs } from '@/components/route/group-rv-bookings/FAQs';
import { GroupRvHero } from '@/components/route/group-rv-bookings/Hero';
import { GroupRvHowItWorks } from '@/components/route/group-rv-bookings/HowItWorks';
import { GroupRvTestimonial } from '@/components/route/group-rv-bookings/Testimonial';
import {
  DEFAULT_WHY_RV_CARDS,
  getWhyRvCardByKey,
  GroupRvWhyRVs,
  SOCCER_CARD,
  TWhyRvCard,
} from '@/components/route/group-rv-bookings/WhyRVs';
import HeadMeta from '@/components/static-pages/HeadMeta';

const GroupRvFilmProductionEventsPage: NextPage = () => {
  const intl = useIntl();

  // Reuse the same SEO copy as the main group bookings page for now.
  const title = intl.formatMessage({
    id: '5ua/pY',
    defaultMessage: 'Group RV Rentals for film productions has never been easier.',
    description: 'Group bookings landing page SEO title',
  });

  const description = intl.formatMessage({
    id: 'YuHfzS',
    defaultMessage:
      "Need 5+ RVs for groups to stay on-site during events? We're your dedicated, stress-free concierge service.",
    description: 'Group bookings landing page SEO description',
  });

  return (
    <>
      <HeadMeta title={title} description={description} />

      <main>
        {/* Hero */}
        <GroupRvHero
          eyebrow="Booking multiple RVs made easy."
          headline="Group RV Rentals for film productions has never been easier."
          body={
            <>
              Need 5+ RVs for your staff during a long-term project?
              <br />
              We&apos;re your dedicated, stress-free concierge service.
            </>
          }
          images={[FilmProductionFull, FilmProductionSmall]}
        />

        {/* How it works */}
        <GroupRvHowItWorks />

        {/* Enquiry form (scroll target for CTAs above) */}
        <GroupRvEnquiryForm page="film-production" />

        {/* Testimonial */}
        <GroupRvTestimonial
          templateType="SubPage"
          subPageKey="staff-housing-coordinator"
          subPageImages={{ primary: StaffRoomImage, secondary: StaffCameraImage }}
        />

        {/* FAQs */}
        <GroupRvFaqs />

        {/* Why RVs section */}
        <GroupRvWhyRVs
          color={true}
          heading="Interested in booking RVs for other types of events? "
          cards={[
            {
              ...getWhyRvCardByKey('weddings'),
              image: HeroImageWeddings as unknown as { src: string },
            } as TWhyRvCard,
            {
              ...getWhyRvCardByKey('cultural-events'),
              image: HeroImageEvents as unknown as { src: string },
            } as TWhyRvCard,
            {
              ...getWhyRvCardByKey('staff-housing'),
              image: HeroImageStaff as unknown as { src: string },
            } as TWhyRvCard,
            SOCCER_CARD,
            ...DEFAULT_WHY_RV_CARDS.filter(
              card =>
                card.key !== 'weddings' &&
                card.key !== 'cultural-events' &&
                card.key !== 'soccer' &&
                card.key !== 'staff-housing' &&
                card.key !== 'film-production',
            ),
          ]}
        />
      </main>
    </>
  );
};

export default GroupRvFilmProductionEventsPage;
